import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface FeatureErrorFallbackProps {
  feature: string;
  onRetry?: () => void;
}

/**
 * Shown when a route-level ErrorBoundary catches an error inside a feature.
 * Offers the user a way to retry (re-mount) or return to the home page.
 */
export const FeatureErrorFallback: React.FC<FeatureErrorFallbackProps> = ({ feature, onRetry }) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        gap: 2,
        textAlign: 'center',
        p: 4,
      }}
    >
      <Typography variant="h5" color="error" fontWeight="bold">
        Something went wrong in {feature}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        An unexpected error occurred. You can try again or go back to the home page.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
        {onRetry && (
          <Button variant="contained" color="primary" onClick={onRetry}>
            Retry
          </Button>
        )}
        <Button variant="outlined" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </Box>
    </Box>
  );
};
